import { useEffect, useRef, useState, useCallback } from 'react';
import { compactWorkflowState } from '@/lib/compactWorkflow';
import { isMultiSheetState } from '@/types/workflow';

// Lager B (JSON-refactor): autosave skriver compact-format istället för
// full state. Compact är ~30-50% mindre och håller samma fidelity (alla
// fält finns kvar, bara förkortade nycklar — n, d, dd, c, i, …). Read-
// pathen i Index.tsx expandar via expandCompactWorkflow.
//
// Storage-key version bumpas från 'workflow-autosave' → '...-v2-compact'
// så att gammal data (full state) inte feltolkas som compact. Migration
// av gamla rader sker en gång i Index.tsx restore-effekt om legacy-key
// hittas (raw-state expandas till compact innan restore).
export const AUTO_SAVE_KEY = 'workflow-autosave-v2-compact';
export const AUTO_SAVE_KEY_LEGACY = 'workflow-autosave';
const AUTO_SAVE_INTERVAL = 30_000; // 30 seconds

function serializeCompact(state: unknown): string {
  // Compact-encode every shape compactWorkflowState understands —
  // multi-sheet (`sheets[]`) AND single-sheet (`blocks[]`). The earlier
  // single-sheet-only guard was a stale safety net from before the
  // multi-sheet branch of compactWorkflowState (`compactWorkflow.ts:410`)
  // existed; multi-sheet round-trips through expand/compact today.
  // Non-workflow values (test fixtures, fallback states) still pass
  // through unchanged so this helper is safe to call indiscriminately.
  if (state && typeof state === 'object') {
    const isMulti = isMultiSheetState(state);
    const isSingle =
      !isMulti && Array.isArray((state as { blocks?: unknown }).blocks);
    if (isMulti || isSingle) {
      try {
        const compact = compactWorkflowState(
          state as Parameters<typeof compactWorkflowState>[0],
        );
        return JSON.stringify(compact);
      } catch {
        // Fallback om compact failar — bevara raw så user inte tappar work.
      }
    }
  }
  return JSON.stringify(state);
}

/**
 * Auto-saves workflow state to localStorage at regular intervals.
 * Tracks dirty state (unsaved changes since last manual save).
 * Warns before page unload if there are unsaved changes.
 *
 * Lager B: state serialiseras som compact-format för 30-50% storleks-
 * reduktion. Både single-sheet och multi-sheet skickas genom
 * compactWorkflowState.
 */
export function useAutoSave(getState: () => unknown) {
  const [isDirty, setIsDirty] = useState(false);
  const lastSavedRef = useRef<string>('');
  const getStateRef = useRef(getState);
  getStateRef.current = getState;

  // Mark as dirty whenever state changes
  const markDirty = useCallback(() => {
    setIsDirty(true);
  }, []);

  // Mark as clean (after manual save)
  const markClean = useCallback(() => {
    setIsDirty(false);
    lastSavedRef.current = serializeCompact(getStateRef.current());
  }, []);

  // Auto-save to localStorage on interval
  useEffect(() => {
    const timer = setInterval(() => {
      const state = getStateRef.current();
      const serialized = serializeCompact(state);
      if (serialized !== lastSavedRef.current) {
        localStorage.setItem(AUTO_SAVE_KEY, serialized);
        lastSavedRef.current = serialized;
      }
    }, AUTO_SAVE_INTERVAL);

    return () => clearInterval(timer);
  }, []);

  // Save on initial mount
  useEffect(() => {
    const state = getStateRef.current();
    lastSavedRef.current = serializeCompact(state);
  }, []);

  // Flush to localStorage vid tab-stängning / refresh / navigation.
  // localStorage.setItem är synkron → garanterat slutförd innan unload
  // även om 30s-timer:en inte hunnit tick:a. Inget preventDefault → ingen
  // popup ("Ändringar kanske inte sparas"-prompten är borttagen).
  //
  // pagehide-event används som backup eftersom beforeunload inte fyrar
  // alltid på iOS Safari/PWA. Båda anropar samma flush.
  useEffect(() => {
    const flush = () => {
      try {
        const serialized = serializeCompact(getStateRef.current());
        if (serialized !== lastSavedRef.current) {
          localStorage.setItem(AUTO_SAVE_KEY, serialized);
          lastSavedRef.current = serialized;
        }
      } catch {
        // Sväla fel — vi blockerar inte unload pga storage-issues.
      }
    };
    window.addEventListener('beforeunload', flush);
    window.addEventListener('pagehide', flush);
    return () => {
      window.removeEventListener('beforeunload', flush);
      window.removeEventListener('pagehide', flush);
    };
  }, []);

  return { isDirty, markDirty, markClean };
}
